import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {live} from 'lit/directives/live.js';
import {connect} from 'pwa-helpers';
import store from '../../store';
import {
	selectMaxUsableBatteryCapacity, selectSolarBatteryType,
	selectSolarDimmProfileData, selectSolarGenerators,
	selectSolarLuminarCountValue,
	selectSolarPVMaxInput, selectSolarSelfSufficiency,
	selectSolarUnitHeight,
	selectSolarVersion,
} from '../solarSelectors';
import {
	selectSolarAkkuKapa,
	selectSolarAkkuladungMax,
	selectSolarAutarkieTage, selectSolarDimmProfileActiveHours,
	selectSolarMaximalMitAutarkie, selectSolarMaximalPv, selectSolarMaxPower, selectSolarNachtlaenge, selectSolarParameters,
	selectTageSafety, selectTageVolleLeistung
} from '../solarSelectorsCalculations';
import {setSolarParameters, setSolarSelfSufficiency} from '../solarSlice';
import {mergeDeep} from '../../util/mergeDeep';
import {selectDebugAeto, selectFamily} from '../../selectors';

@customElement('solar-assembly-debug')
export class solarAssembly extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				position: absolute;
				bottom: 10px;
				left: 0;
				right: 0;
				margin-inline: auto;
				width: fit-content;
				z-index: 1;
			}

			.debug {
				padding: 5px;
				background-color: #5a1c1c;
				color: white;
				font-family: monospace;
				font-size: 10px;
			}

			table, th, td {
				border: 1px solid black;
				border-collapse: collapse;
			}

			th, td {
				text-align: left;
				vertical-align: top;
				padding: 2px 4px;

			}
			input {
				color: white;
				border: none;
				background-color: transparent;
				width: 60px;
			}

		`];

	@property({type: Object})
	solarSelfSufficiency = {};

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.solar = state.solar.data;
		this.family = selectFamily(state);
		this.dimmProfile = selectSolarDimmProfileData(this.state);
		this.solarGenerators = parseInt(selectSolarUnitHeight(state), 10);
		this.luminarCount = this.solar.project.luminarCount;
		this.orientation = this.solar.project.orientation;
		this.inputData = this.getInputData();
		this.generators = selectSolarGenerators(state);
		//this.outputData = this.getOutputData();
		this.isBasic = selectSolarVersion(state) === 'AetoBasic';
		this.solarSelfSufficiency = selectSolarSelfSufficiency(state);
		this.solarVersion = selectSolarVersion(state);
		this.lumCount = selectSolarLuminarCountValue(state) ?? 1;
		this.debug = selectDebugAeto(state);
	}

	getInputData() {
		if (this.plotData) {
			// return minimal and maximal values from y
			const min = Math.floor(Math.min(...this.plotData[0].y));
			const max = Math.ceil(Math.max(...this.plotData[0].y));
			return {min: min, max: max};
		} else {
			return {min: 0, max: 0};
		}
	}

	getOutputData() {
		return {
			min: Math.min(
				selectSolarPVMaxInput(this.state)[this.generators - 1].winter,
				selectMaxUsableBatteryCapacity(this.state)
			),
			max: Math.min(
				selectSolarPVMaxInput(this.state)[this.generators - 1].summer,
				selectMaxUsableBatteryCapacity(this.state)
			)
		};
	}
	onParameterChange(value) {
		const newParameters = mergeDeep(selectSolarParameters(this.state), value);
		store.dispatch(setSolarParameters({parameters: newParameters}));
	}
	onSelfSufficiencyChange(value, season) {
		// clone this.solarSelfSufficiency
		const newSelfSufficiency = JSON.parse(JSON.stringify(this.solarSelfSufficiency));

		newSelfSufficiency[this.solarVersion][season] = parseInt(value, 10);

		if (!isNaN(parseInt(value, 10))) {
			if (this.solarVersion === 'AetoBasic') {
				// if basic, set all seasons to the same value
				newSelfSufficiency[this.solarVersion].summer = parseInt(value, 10);
				newSelfSufficiency[this.solarVersion].spring = parseInt(value, 10);
				newSelfSufficiency[this.solarVersion].winter = parseInt(value, 10);
			} else {
				newSelfSufficiency[this.solarVersion][season] = parseInt(value, 10);
			}
			store.dispatch(setSolarSelfSufficiency( { selfSufficiency: newSelfSufficiency }));
		}
	}

	pvDataComplete() {
		return this.orientation?.front.pvgisDaily !== null &&
			this.orientation?.back.pvgisDaily !== null &&
			this.orientation?.left.pvgisDaily !== null &&
			this.orientation?.right.pvgisDaily !== null;
	}

	render() {
		if (!this.debug || !this.pvDataComplete() || this.family !== 'Aeto') return null;

		const s = this.state;
		const a = selectSolarAutarkieTage(s);
		const c = selectSolarAkkuladungMax(s);
		const h = selectSolarDimmProfileActiveHours(s);
		const n = selectSolarNachtlaenge(s);
		const f1 = selectSolarMaximalMitAutarkie(s);
		const pvIn = selectSolarPVMaxInput(s)[this.generators - 1];
		const f2 = selectSolarMaximalPv(s);
		const f3 = selectSolarMaxPower(s);
		const tageVolleLeistung = selectTageVolleLeistung(s);
		const tageSafety = selectTageSafety(s);

		const f1Rounded = {
			winter: Math.round(f1.winter * 100) / 100,
			spring: Math.round(f1.spring * 100) / 100,
			summer: Math.round(f1.summer * 100) / 100
		};
		const f2Rounded = {
			winter: Math.round(f2.winter * 100) / 100,
			spring: Math.round(f2.spring * 100) / 100,
			summer: Math.round(f2.summer * 100) / 100
		};
		const f3Rounded = {
			winter: Math.round(f3.winter * 100) / 100,
			spring: Math.round(f3.spring * 100) / 100,
			summer: Math.round(f3.summer * 100) / 100
		};

		if (!pvIn || !f2 || !f3) return null;

		return html`
			<div class="solarAssemblyDebug">
				<div class="debug">
					<table>
						<tr>
							<th></th>
							<td>Winter</td>
							<td>Frühling</td>
							<td>Sommer</td>
						</tr>
						<tr>
							<th>Sun:</th>
							<td>${n.winter.sunrise} - ${n.winter.sunset}</td>
							<td>${n.spring.sunrise} - ${n.spring.sunset}</td>
							<td>${n.summer.sunrise} - ${n.summer.sunset}</td>
						</tr>
						<tr>
							<th>Nachtlänge:</th>
							<td>${Math.round(n.winter.nightLength * 100) / 100}</td>
							<td>${Math.round(n.spring.nightLength * 100) / 100}</td>
							<td>${Math.round(n.summer.nightLength * 100) / 100}</td>
						</tr>
						<tr>
							<td>PV Ertrag (unbegrenzt):</td>
							<td>${pvIn.winter}</td>
							<td>${pvIn.spring}</td>
							<td>${pvIn.summer}</td>
						</tr>
						<tr>
							<td colspan="4"><br></td>
						</tr>
						<tr>
							<th>Version:</th>
							<td colspan="3">${selectSolarVersion(s)}</td>
						</tr>
						<tr>
							<th>Autarkietage:</th>
							<td>
								<input type="number" step="1" min="0"
									   .value="${live(selectSolarSelfSufficiency(s)[this.solarVersion].winter)}"
									   @change="${(e) => this.onSelfSufficiencyChange(parseFloat(e.target.value), 'winter')}">
							</td>
							<td>
								<input type="number" step="1" min="0"
									   .value="${live(selectSolarSelfSufficiency(s)[this.solarVersion].spring)}"
									   @change="${(e) => this.onSelfSufficiencyChange(parseFloat(e.target.value), 'spring')}">
							</td>
							<td>
								<input type="number" step="1" min="0"
									   .value="${live(selectSolarSelfSufficiency(s)[this.solarVersion].summer)}"
									   @change="${(e) => this.onSelfSufficiencyChange(parseFloat(e.target.value), 'summer')}">
							</td>
						</tr>
						<tr>
							<th>AkkuKapa:</th>
							<td colspan="3">${selectSolarAkkuKapa(s)}</td>
						</tr>
						<tr>
							<th>Akkuladung max:</th>
							<td>
								<input type="number" step="0.01" min="0"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].maxBatteryCharge.winter)}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {maxBatteryCharge: {winter: parseFloat(e.target.value)}}})}">
							</td>
							<td>
								<input type="number" step="0.01" min="0"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].maxBatteryCharge.spring)}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {maxBatteryCharge: {spring: parseFloat(e.target.value)}}})}">
							</td>
							<td>
								<input type="number" step="0.01" min="0"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].maxBatteryCharge.summer)}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {maxBatteryCharge: {summer: parseFloat(e.target.value)}}})}">
							</td>
						</tr>
						<tr>
							<th>Power Limit:</th>
							<td colspan="3">
								<input type="number" step="1"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].powerLimit)}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {powerLimit: parseInt(e.target.value, 10)}})}">
							</td>
						</tr>
						<tr>
							<th>Start Safety:</th>
							<td colspan="3">
								<input type="number" step="0.01"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].startSafety)}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {startSafety: parseFloat(e.target.value)}})}">
							</td>
						</tr>
						<tr>
							<th>DeepDischargeLimit:</th>
							<td colspan="3">
								<input type="number" step="0.01"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].deepDischargeLimit)}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {deepDischargeLimit: parseFloat(e.target.value)}})}">
							</td>
						</tr>
						<tr>
							<th>Eigenverbrauch:</th>
							<td colspan="3">
								<input type="number" step="0.01"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].selfConsumption[this.lumCount])}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {selfConsumption: { [this.lumCount]: parseFloat(e.target.value)}}})}">
							</td>
						</tr>
						<tr>
							<th>Active Hours (dimmprofil):</th>
							<td>${Math.round(h.winter * 100) / 100}</td>
							<td>${Math.round(h.spring * 100) / 100}</td>
							<td>${Math.round(h.summer * 100) / 100}</td>
						</tr>
						<tr>
							<th>Dimm Safety:</th>
							<td colspan="3">
								<input type="number" step="0.01"
									   .value="${live(selectSolarParameters(s)[this.solarVersion].dimmSafety)}"
									   @change="${(e) => this.onParameterChange({[this.solarVersion]: {dimmSafety: parseFloat(e.target.value)}})}">
							</td>
						</tr>
						<tr>
							<td colspan="4"><br></td>
						</tr>
						<tr>
							<th>max Power (mit Autarkie):</th>
							<td>${this.lumCount} x ${f1Rounded.winter}</td>
							<td>${this.lumCount} x ${f1Rounded.spring}</td>
							<td>${this.lumCount} x ${f1Rounded.summer}</td>
						</tr>
						<tr>
							<th>max Power (PV):</th>
							<td>${this.lumCount} x ${f2Rounded.winter}</td>
							<td>${this.lumCount} x ${f2Rounded.spring}</td>
							<td>${this.lumCount} x ${f2Rounded.summer}</td>
						</tr>
						<tr>
							<th>max Power:</th>
							<td>${this.lumCount} x ${f3Rounded.winter}</td>
							<td>${this.lumCount} x ${f3Rounded.spring}</td>
							<td>${this.lumCount} x ${f3Rounded.summer}</td>
						</tr>
						<tr>
							<th>Tage bei voller Leistung:</th>
							<td>${Math.round(tageVolleLeistung.winter * 100) / 100}</td>
							<td>${Math.round(tageVolleLeistung.spring * 100) / 100}</td>
							<td>${Math.round(tageVolleLeistung.summer * 100) / 100}</td>
						</tr>
						<tr>
							<th>Tage im Safetymodus:</th>
							<td>${Math.round(tageSafety.winter * 100) / 100}</td>
							<td>${Math.round(tageSafety.spring * 100) / 100}</td>
							<td>${Math.round(tageSafety.summer * 100) / 100}</td>
						</tr>
						<tr>
							<th>Autarkietage Gesamt:</th>
							<td>${Math.round((tageVolleLeistung.winter + tageSafety.winter) * 100) / 100}</td>
							<td>${Math.round((tageVolleLeistung.spring + tageSafety.spring) * 100) / 100}</td>
							<td>${Math.round((tageVolleLeistung.summer + tageSafety.summer) * 100) / 100}</td>
						</tr>
					</table>
				</div>
			</div>
		`;

	}
}
